import 'babel-polyfill';
import 'whatwg-fetch';
import DataMap from 'datamaps';
import * as d3 from 'd3';
import getTarget from './utils/get-target';
import { chartDataSelector, countryRankSelector } from './selectors';

const COLORS = {
  LOW: '#c6e4f3',
  MEDIUM: '#89c9cb',
  HIGH: '#2c9f93',
  MAX: '#076b6a',
};

const TABS = {
  de: {
    index: 'Der Index aller Länder',
    policyActivity: 'Die Policy-Aktivität aller Länder',
    digitalReadiness: 'Die Digitale Readiness aller Länder',
    actualUsage: 'Die Tatsächliche Nutzung aller Länder',
  },
  en: {
    index: 'Digital Health Index',
    policyActivity: 'Policy activity',
    digitalReadiness: 'Digital Readiness',
    actualUsage: 'Actual use of data',
  },
};

const LABELS = {
  de: {
    rank: 'Rang',
    index: 'Indexwert',
    policyActivity: 'Policy-Aktivität',
    digitalReadiness: 'Digitale Readiness',
    actualUsage: 'Tatsächliche Datennutzung',
  },
  en: {
    rank: 'Rank',
    index: 'Index',
    policyActivity: 'Policy activity',
    digitalReadiness: 'Digital Readiness',
    actualUsage: 'Actual use of data',
  },
};

const getFillKey = (country, tab) => {
  if (country[tab] == null) {
    return undefined;
  }
  if (country[tab] < 25) {
    return 'LOW';
  }
  if (country[tab] < 50) {
    return 'MEDIUM';
  }
  if (country[tab] < 75) {
    return 'HIGH';
  }
  return 'MAX';
};

class EHealthMap {
  constructor(tiles, $el, mq, apiUrl, lang) {
    this.$el = $el;
    this.tiles = tiles;
    this.isDesktop = mq.matches;
    this.apiUrl = apiUrl;
    this.lang = lang;

    this.tab = 'index';
    this.maps = [];
    this.$barChartContainer = this.$el.querySelector('.ehealth-map__bars');

    this.init();
  }

  deconstructor() {
    this.$el.removeEventListener('click', this.handleTabClick);
    this.maps.forEach((datamap) => {
      datamap.svg.remove();
    });

    window.removeEventListener('resize', this.handleResize);
    this.$el.removeEventListener('click', this.handleTabClick);
  }

  handleTabClick = (event) => {
    const target = getTarget(event.target, '.tab-bar__item');

    if (target) {
      event.preventDefault();
      this.tab = target.dataset.tab;
      this.setFillKeys();

      this.maps.forEach(map => map.updateChoropleth(this.data));

      const barsHeadline = this.$el.querySelector('.ehealth-map__bars-headline');

      barsHeadline.innerText = TABS[this.lang][this.tab];

      this.updateBarChart();
    }
  };

  popupTemplate = (geo, data) => (!data
    ? null
    : `
      <div class='ehealth-map__popup'>
        <div class='ehealth-map__popup-header'>
          <strong>${data.name}</strong>
          <div class='ehealth-map__popup-rank-wrap'>
            <div class='ehealth-map__popup-rank-wrap-inner'>
              <span class='ehealth-map__popup-rank'>
    ${countryRankSelector({
      data: this.data,
      tab: this.tab,
      name: data.name,
    })}.
              </span>
              <span class='ehealth-map__popup-rank-text'>${LABELS[this.lang].rank}</span>
            </div>
          </div>
        </div>
        <div class='ehealth-map__popup-body'>
          <table class='ehealth-map__table'>
            <tbody class='ehealth-map__table-body'>
              <tr class='ehealth-map__table-row'>
                <td>${LABELS[this.lang].policyActivity}</td>
                <td>${data.policyActivity}</td>
              </tr>
              <tr class='ehealth-map__table-row'>
                <td>${LABELS[this.lang].digitalReadiness}</td>
                <td>${data.digitalReadiness}</td>
              </tr>
              <tr class='ehealth-map__table-row'>
                <td>${LABELS[this.lang].actualUsage}</td>
                <td>${data.actualUsage}</td>
              </tr>
            </tbody>
            <tfoot class='ehealth-map__table-footer'>
              <tr class='ehealth-map__table-row'>
                <td><strong>${LABELS[this.lang].index}</strong></td>
                <td><strong>${data.index}</strong></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    `);

  setFillKeys = () => {
    Object.keys(this.data).forEach((countryKey) => {
      this.data[countryKey].fillKey = getFillKey(this.data[countryKey], this.tab);
    });
  };

  async getData() {
    const countryData = await fetch(this.apiUrl);
    this.data = await countryData.json();
  }

  handleResize = () => {
    this.maps.forEach(datamap => datamap.resize());

    while (this.$barChartContainer.lastChild) {
      this.$barChartContainer.removeChild(this.$barChartContainer.lastChild);
    }

    this.createBarChart();
  };

  async init() {
    await this.getData();
    this.setFillKeys();
    this.createMaps();
    this.createBarChart();

    window.addEventListener('resize', this.handleResize);

    this.$el.addEventListener('click', this.handleTabClick);
  }

  createBarChart = () => {
    const margin = this.isDesktop
      ? { bottom: 30, top: 0, left: 0 }
      : { bottom: 0, top: 10, left: 30 };
    const { offsetHeight, offsetWidth } = this.$barChartContainer;
    this.barChartHeight = offsetHeight - margin.bottom - margin.top;
    this.barChartWidth = offsetWidth;

    this.$svg = d3
      .select(this.$barChartContainer)
      .append('svg')
      .attr('height', offsetHeight)
      .attr('width', offsetWidth)
      .append('g')
      .attr('class', 'ehealth-map__bars-container')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    this.$svg
      .append('g')
      .attr('class', 'ehealth-map__x-axis')
      .attr('transform', `translate(0, ${this.isDesktop ? this.barChartHeight : 0})`);

    this.$bars = this.$svg.append('g').attr('class', 'ehealth-map__bars-group');

    this.updateBarChart();
  };

  updateBarChart = () => {
    const barChartData = chartDataSelector({
      data: this.data,
      tab: this.tab,
      isDesktop: this.isDesktop,
    });

    this.x = d3.scale
      .ordinal()
      .domain(barChartData.map(d => d.countryKey))
      .rangeRoundBands([0, this.isDesktop ? this.barChartWidth : this.barChartHeight], 0.2);

    this.y = d3.scale
      .linear()
      .domain([0, 130])
      .range([this.isDesktop ? this.barChartHeight : this.barChartWidth, 0]);

    // X- and Y-Axis are switched on Desktop and Mobile but variable name stays the same
    this.xAxis = d3.svg
      .axis()
      .scale(this.x)
      .orient(this.isDesktop ? 'bottom' : 'left');

    this.$svg.select('.ehealth-map__x-axis').call(this.xAxis);

    this.barGroup = this.$bars.selectAll('.ehealth-map__bar').data(barChartData);

    // update bars
    const barEnter = this.barGroup
      .enter()
      .append('g')
      .attr('class', 'ehealth-map__bar');

    this.barGroup
      .select('rect')
      .attr('class', d => `ehealth-map__bar-rect ehealth-map__bar-rect--${d.countryKey}`)
      .attr('style', d => `fill: #${COLORS[d.fillKey].substr(1)};`)
      .transition()
      .duration(1000)
      .attr('x', this.getRectX)
      .attr('y', this.getRectY)
      .attr('width', this.getRectWidth)
      .attr('height', this.getRectHeight);

    this.barGroup
      .select('text')
      .transition()
      .duration(1000)
      .attr('x', this.getTextX)
      .attr('y', this.getTextY)
      .text(d => (d[this.tab] ? d[this.tab] : ''));

    barEnter
      .append('rect')
      .attr('class', d => `ehealth-map__bar-rect ehealth-map__bar-rect--${d.countryKey}`)
      .attr('style', d => `fill: #${COLORS[d.fillKey].substr(1)};`)
      .attr('x', this.getRectX)
      .attr('y', this.getRectY)
      .attr('width', this.getRectWidth)
      .attr('height', this.getRectHeight)
      .on('mouseenter', (d) => {
        this.maps.forEach(map => map.updateChoropleth({ [d.countryKey]: '#ff8c60' }));
        this.setRectActive(d.countryKey);
      })
      .on('mouseleave', (d) => {
        this.maps.forEach(map => map.updateChoropleth(this.data));
        this.unsetRectActive(d.countryKey);
      })
      .on('click', d => this.openMoreLink(d.countryKey));

    barEnter
      .append('text')
      .attr('class', 'ehealth-map__bar-text label')
      .attr('x', this.getTextX)
      .attr('y', this.getTextY)
      .text(d => (d[this.tab] ? d[this.tab] : ''));
  };

  openMoreLink = (key) => {
    const location = this.data[key].moreLink;
    window.location.assign(location);
  };

  unsetRectActive = (key) => {
    const barClassSelector = `ehealth-map__bar-rect--${key}`;
    this.barGroup
      .select(`.${barClassSelector}`)
      .attr('class', `ehealth-map__bar-rect ${barClassSelector}`);
  };

  setRectActive = (key) => {
    const barClassSelector = `ehealth-map__bar-rect--${key}`;
    this.barGroup
      .select(`.${barClassSelector}`)
      .attr('class', `ehealth-map__bar-rect ${barClassSelector} ehealth-map__bar-rect--active`);
  };

  getTextY = (d) => {
    const {
      isDesktop, y, tab, x,
    } = this;

    return isDesktop ? y(d[tab]) - 5 : x(d.countryKey) + 17;
  };

  getTextX = (d) => {
    const {
      isDesktop, x, barChartWidth, y, tab,
    } = this;

    return isDesktop ? x(d.countryKey) + x.rangeBand() / 2 : barChartWidth - y(d[tab]) + 15;
  };

  getRectHeight = (d) => {
    const {
      isDesktop, barChartHeight, y, tab, x,
    } = this;

    return isDesktop ? barChartHeight - y(d[tab]) : x.rangeBand();
  };

  getRectWidth = (d) => {
    const {
      isDesktop, x, barChartWidth, y, tab,
    } = this;
    return isDesktop ? x.rangeBand() : barChartWidth - y(d[tab]);
  };

  getRectX = (d) => {
    const { isDesktop, x } = this;

    return isDesktop ? x(d.countryKey) : 0;
  };

  getRectY = (d) => {
    const {
      isDesktop, y, x, tab,
    } = this;

    return isDesktop ? y(d[tab]) : x(d.countryKey);
  };

  createMaps() {
    this.tiles.forEach((tile) => {
      this.maps.push(
        new DataMap({
          element: document.querySelector(tile.element),
          scope: 'world',
          projection: 'orthographic',
          fills: {
            defaultFill: '#cecece',
            ...COLORS,
          },
          data: this.data,
          geographyConfig: {
            popupTemplate: this.popupTemplate,
          },
          setProjection: (element) => {
            const projection = d3.geo
              .mercator()
              .center(tile.center)
              .scale(tile.scale)
              .translate([element.offsetWidth / 2, element.offsetHeight / 2]);
            const path = d3.geo.path().projection(projection);

            return { path, projection };
          },
          responsive: true,
          aspectRatio: 1,
          done: (datamap) => {
            datamap.svg
              .selectAll('.datamaps-subunit')
              .on('mouseenter', geography => this.setRectActive(geography.id));

            datamap.svg
              .selectAll('.datamaps-subunit')
              .on('mouseleave', geography => this.unsetRectActive(geography.id));

            datamap.svg
              .selectAll('.datamaps-subunit')
              .on('click', geography => this.openMoreLink(geography.id));
          },
        }),
      );
    });
  }
}

const tiles = [
  {
    element: '.ehealth-map__america',
    center: [-100, 72],
    scale: 170,
  },
  {
    element: '.ehealth-map__europe',
    center: [10, 55],
    scale: 330,
  },
  {
    element: '.ehealth-map__australia',
    center: [132, -25],
    scale: 420,
  },
];

let componentStore = [];

function onMediaQueryChange(mq) {
  componentStore.forEach(component => component.deconstructor && component.deconstructor());
  componentStore = [];

  document.querySelectorAll('.ehealth-map').forEach(($el) => {
    const { apiUrl, lang } = $el.dataset;

    if (apiUrl) {
      componentStore.push(new EHealthMap(tiles, $el, mq, apiUrl, lang));
    }
  });
}

const mql = window.matchMedia('(min-width: 900px)');

mql.addListener(onMediaQueryChange);

onMediaQueryChange(mql);
